import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MadafricaLogo } from "../components/MadafricaLogo";
import { CityPicker } from "../components/CityPicker";
import { Mail, Lock, User, UserPlus, Eye, Mic2, ArrowRight } from "lucide-react";

/**
 * Étape 1 : choix du type de compte (visiteur ou artiste)
 * Étape 2 : formulaire d'inscription contextualisé selon le choix
 */
export const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Lecture du query param ?as=artist ou ?as=visitor pour pré-sélectionner
  const initialMode = (() => {
    const as = searchParams.get("as");
    if (as === "artist") return "artist";
    if (as === "visitor") return "visitor";
    return null; // pas de choix → on affiche l'écran de sélection
  })();

  const [accountType, setAccountType] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({
        name,
        email,
        password,
        is_artist: accountType === "artist",
        city,
        country: "Madagascar",
      });
      toast.success(
        accountType === "artist"
          ? "Bienvenue sur Voamada artiste ! Publie ton premier contenu depuis le dashboard 🎤"
          : "Bienvenue sur Voamada ! Découvre les talents gasy 🇲🇬"
      );
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inscription échouée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="register-page">
      <div className="w-full max-w-xl">
        <Link to="/" className="block mb-10 text-center">
          <div className="inline-block"><MadafricaLogo size={96} withText showTopMadaBadge={false} /></div>
        </Link>

        {accountType === null ? (
          <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-8" data-testid="register-type-choice">
            <h1 className="font-display text-3xl font-black tracking-tight mb-2">Rejoignez Voamada</h1>
            <p className="text-white/60 mb-6">Dites-nous qui vous êtes pour commencer :</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <TypeCard
                icon={Eye}
                title="Visiteur"
                subtitle="Je veux découvrir les artistes"
                perks={["Écouter & liker", "Suivre des artistes", "Playlists & chat"]}
                onClick={() => setAccountType("visitor")}
                color="from-brand-yellow/15 to-brand-orange/10 border-brand-yellow/40"
                accent="text-brand-yellow"
                testid="register-type-visitor"
              />
              <TypeCard
                icon={Mic2}
                title="Artiste"
                subtitle="Je veux publier mes créations"
                perks={["Profil vérifiable", "Upload audio / vidéo", "Stats & fanbase"]}
                onClick={() => setAccountType("artist")}
                color="from-brand-red/15 to-brand-orange/15 border-brand-red/40"
                accent="text-brand-red"
                testid="register-type-artist"
                highlight
              />
            </div>

            <p className="text-sm text-white/60 text-center mt-7">
              Déjà inscrit ?{" "}
              <Link to="/login" className="text-brand-red hover:text-brand-redHover font-semibold" data-testid="goto-login">
                Connectez-vous
              </Link>
            </p>
          </div>
        ) : (
          <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-8" data-testid="register-form-block">
            {/* Badge + back */}
            <div className="flex items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => setAccountType(null)}
                className="text-xs text-white/50 hover:text-white inline-flex items-center gap-1"
                data-testid="register-back-type"
              >
                ← Changer
              </button>
              <span className={`ml-auto tag-pill ${accountType === "artist" ? "bg-brand-red/15 text-brand-red border-brand-red/30" : "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30"}`}>
                {accountType === "artist" ? "🎤 Compte artiste" : "👁️ Compte visiteur"}
              </span>
            </div>

            <h1 className="font-display text-3xl font-black tracking-tight mb-2">
              {accountType === "artist" ? "Crée ton profil d'artiste" : "Rejoins la communauté"}
            </h1>
            <p className="text-white/60 mb-7">
              {accountType === "artist"
                ? "Publie, fédère tes fans et fais briller ton talent 🇲🇬"
                : "Découvre, like et partage les talents de demain ✨"}
            </p>

            <form onSubmit={submit} className="space-y-4">
              <InputField
                icon={<User size={16} />}
                type="text"
                placeholder={accountType === "artist" ? "Ton nom d'artiste" : "Ton prénom / pseudo"}
                value={name}
                onChange={setName}
                testid="register-name"
              />
              <InputField icon={<Mail size={16} />} type="email" placeholder="Email" value={email} onChange={setEmail} testid="register-email" />
              <InputField icon={<Lock size={16} />} type="password" placeholder="Mot de passe (min 6)" value={password} onChange={setPassword} testid="register-password" />

              <CityPicker value={city} onChange={setCity} placeholder="D'où viens-tu ? (Ville / village 🇲🇬)" testid="register-city" />

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                <UserPlus size={16} />
                {loading
                  ? "Création..."
                  : accountType === "artist"
                    ? "Créer mon profil artiste"
                    : "Créer mon compte visiteur"}
              </button>
            </form>

            <p className="text-sm text-white/60 text-center mt-6">
              Déjà inscrit ?{" "}
              <Link to="/login" className="text-brand-red hover:text-brand-redHover font-semibold" data-testid="goto-login">
                Connectez-vous
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const TypeCard = ({ icon: Icon, title, subtitle, perks, onClick, color, accent, highlight, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left relative rounded-2xl border bg-gradient-to-br ${color} p-5 transition-all hover:-translate-y-0.5 hover:shadow-2xl group ${highlight ? "ring-1 ring-brand-red/30" : ""}`}
    data-testid={testid}
  >
    {highlight && (
      <span className="absolute -top-2 right-3 text-[9px] font-black uppercase tracking-widest bg-brand-red text-white px-2 py-0.5 rounded-full">
        Populaire
      </span>
    )}
    <Icon size={26} className={`${accent} mb-3`} />
    <h3 className="font-display text-2xl font-black mb-1">{title}</h3>
    <p className="text-xs text-white/60 mb-3">{subtitle}</p>
    <ul className="space-y-1 mb-4">
      {perks.map((p) => (
        <li key={p} className="text-[11px] text-white/70 flex items-center gap-1.5">
          <span className={`w-1 h-1 rounded-full ${accent === "text-brand-red" ? "bg-brand-red" : "bg-brand-yellow"}`} />
          {p}
        </li>
      ))}
    </ul>
    <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider ${accent} group-hover:translate-x-0.5 transition-transform`}>
      Choisir <ArrowRight size={12} />
    </span>
  </button>
);

const InputField = ({ icon, type, placeholder, value, onChange, testid }) => (
  <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 focus-within:border-brand-red/50">
    <span className="text-white/40">{icon}</span>
    <input
      required
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="flex-1 bg-transparent outline-none text-sm"
      data-testid={`${testid}-input`}
    />
  </div>
);

export default RegisterPage;
